import React, { createContext, useState, useEffect, useCallback } from 'react';
import localStorageService from '../services/storage/localStorage.service';

// Cria o contexto
export const ThemeContext = createContext({});

/**
 * Provider de tema
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  /**
   * Carrega tema salvo ao montar componente
   */
  useEffect(() => {
    const savedTheme = localStorageService.getTheme();
    
    if (savedTheme) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Sem preferência salva: padrão é light
      applyTheme('light');
    }
  }, []);

  /**
   * Aplica tema ao documento
   * @param {string} themeName - 'light' ou 'dark'
   */
  const applyTheme = (themeName) => {
    if (themeName === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  /**
   * Alterna entre light e dark
   */
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorageService.setTheme(newTheme);
  }, [theme]);

  /**
   * Define tema específico
   * @param {string} newTheme - 'light' ou 'dark'
   */
  const setThemeMode = useCallback((newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      console.warn('Tema inválido. Use "light" ou "dark".');
      return;
    }
    
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorageService.setTheme(newTheme);
  }, []);

  /**
   * Alterna sidebar
   */
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  /**
   * Define estado da sidebar
   * @param {boolean} isOpen
   */
  const setSidebarState = useCallback((isOpen) => {
    setSidebarOpen(isOpen);
  }, []);

  /**
   * Reseta para tema do sistema
   */
  const resetToSystemTheme = useCallback(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemTheme = prefersDark ? 'dark' : 'light';
    
    setTheme(systemTheme);
    applyTheme(systemTheme);
    localStorageService.setTheme(systemTheme);
  }, []);

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme: setThemeMode,
    resetToSystemTheme,
    sidebarOpen,
    toggleSidebar,
    setSidebarState,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};